import React, { useReducer, useState, Reducer, useEffect, MouseEvent } from 'react';
import 'react-datepicker/dist/react-datepicker.css';
import { compareAsc, format, isBefore } from 'date-fns';
import styled, { css, createGlobalStyle } from 'styled-components';

import type { ReferenceSearchCalendarDuration } from '@lt/api/dist/methods/references/types';
import type { SearchType } from '@lt/api/dist/types/searchParams';
import type { MobileProps, DatePickerButton } from '../../types';
import type { VacationOptionsPresets } from './types';

import { SEARCH_TYPE_NAMES } from '../../constants';

import {
  Action,
  changeDates,
  changeShiftAction,
  setEndDateFocus,
  setStartDateFocus,
  setStartDatePreset,
  setNightsPreset,
  setNights,
  DatePickerState,
  initialState,
  reducer,
  updateDatepickerState,
  VacationOptions,
  trackButtonClick,
} from './state';
import { useFetchIndication } from '../../hooks/useFetchIndication';

import { DurationTrip } from '@lt/components/components/DurationTrip/DurationTrip';
import DatePickerTabs from './components/DatePickerTabs';

import { calcMinDate } from './utils';

type DatePickerProps = {
  onChange?: (value: DatePickerState) => void;
  onPanelChange?: (value: { left: Date | null; right: Date | null }) => void;
  onClose: () => void;
  initialValue?: DatePickerState;
  searchCalendarDuration: ReferenceSearchCalendarDuration[];
  open?: boolean;
  searchType: SearchType;
  className?: string;
  unavailableDates?: Date[];
  arrivalDatesDict?: Record<string, string[]>;
  variant?: 'default' | 'hotel';
  departureId?: number;
  placeId?: number;
  vacationOptionsPresets: VacationOptionsPresets;
  isOpenInitial?: boolean;
} & MobileProps;

export const DatePicker = ({
  onChange,
  isMobile = false,
  isWizard = false,
  initialValue,
  open,
  onClose,
  unavailableDates = [],
  arrivalDatesDict = {},
  searchType,
  className,
  variant = 'default',
  searchCalendarDuration,
  onPanelChange,
  departureId,
  placeId,
  vacationOptionsPresets,
  isOpenInitial = false,
}: DatePickerProps): JSX.Element => {
  const initial = { ...initialState, ...(initialValue || {}) };

  const [state, dispatch] = useReducer<Reducer<DatePickerState, Action>>(reducer, initial);
  const {
    left,
    right,
    shift,
    inputFocuses = { isStartDateFocus: false, isEndDateFocus: false },
    startDatePreset,
    nightsPreset,
    nights,
    initialTab,
    buttonsClicked,
  } = state;
  const { date: startDate } = left;
  const { date: endDate } = right;
  const { isStartDateFocus, isEndDateFocus } = inputFocuses;

  useEffect(() => {
    if ((isWizard || isOpenInitial) && !isStartDateFocus && !isEndDateFocus) {
      dispatch(setStartDateFocus());
    }
  }, [isWizard, isOpenInitial, state]);

  useEffect(() => {
    if (onPanelChange) {
      onPanelChange({ left: startDate, right: endDate });
    }
  }, [startDate, endDate]);

  const openCalendar = () => {
    if (!isWizard) {
      setOpen(true);
    }
  };

  const closeCalendar = (event: MouseEvent<HTMLElement | SVGSVGElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setOpen(false);
    onClose();
  };

  const handleClickStartInput = () => {
    dispatch(setStartDateFocus());
    openCalendar();
  };
  const handleClickEndInput = () => {
    dispatch(setEndDateFocus());
    openCalendar();
  };

  const handleEndDateClear = () => {
    dispatch(changeDates({ startDate, endDate: null }));
  };

  const handleButtonClick = (buttonName: DatePickerButton) => {
    dispatch(trackButtonClick(buttonName));
  };

  const handleStartDateClear: React.MouseEventHandler<HTMLSpanElement> = (event) => {
    dispatch(changeDates({ startDate: null, endDate: null }));

    // Хак на закрытие календаря, чтобы корректно отображались первые месяцы после сброса
    closeCalendar(event);
  };

  const [localOpen, setOpen] = useState(false);

  const minDate = calcMinDate(searchType);

  const arrivalDates =
    (startDate &&
      arrivalDatesDict[format(startDate, 'yyyy-MM-dd')]?.map(
        (dateString) => new Date(dateString)
      )) ||
    [];

  const unavailableBeforeDates = startDate
    ? unavailableDates.filter((unavailableDate) => isBefore(unavailableDate, startDate))
    : [];
  const excludeArrivalAndShiftDates = [...arrivalDates, ...unavailableBeforeDates];

  const excludeDates = isEndDateFocus ? excludeArrivalAndShiftDates : unavailableDates;

  const vacationOptionSelected = !!(startDatePreset || nightsPreset || nights);

  useEffect(() => {
    setOpen(!!open);
    if (open) {
      dispatch(setStartDateFocus());
    }
  }, [open]);

  useEffect(() => {
    dispatch(updateDatepickerState(initial));
  }, [initialValue]);

  const changeShift = (shift: number) => dispatch(changeShiftAction(shift));

  const onChangeCalendar = (date: Date | null) => {
    const start = isStartDateFocus ? date : startDate;
    const end = isEndDateFocus ? date : endDate;
    const isReset =
      vacationOptionSelected || (start && end && compareAsc(new Date(start), new Date(end)) > 0);

    const saveStart = isReset ? date : start;
    const saveEnd = isReset ? null : end;

    const abortCondition =
      saveStart &&
      saveEnd &&
      format(new Date(saveStart), 'ddMMyy') === format(new Date(saveEnd), 'ddMMyy');

    if (abortCondition) return;

    if (!isReset && isStartDateFocus) {
      dispatch(setEndDateFocus());
      dispatch(changeDates({ startDate: saveStart, endDate: null }));
    }
    if (!isReset && isEndDateFocus) {
      dispatch(setStartDateFocus());
      dispatch(changeDates({ startDate: saveStart, endDate: saveEnd }));
    }
    if (isReset) {
      dispatch(setEndDateFocus());
      dispatch(changeDates({ startDate: saveStart, endDate: null }));
    }
  };

  const onChangeVacationOption = ({ startDatePreset, nightsPreset, nights }: VacationOptions) => {
    if (startDatePreset) {
      dispatch(
        setStartDatePreset({
          startDatePreset,
          recommendedNightsPreset: vacationOptionsPresets.recommendedNightsPreset,
        })
      );
    }

    if (nightsPreset) {
      dispatch(setNightsPreset({ nightsPreset }));
    }

    if (nights) {
      dispatch(setNights({ nights }));
    }
  };

  const closeCalendarHandler = (event: MouseEvent<HTMLElement | SVGSVGElement>) => {
    applyButtonHandler(event);
  };

  const applyButtonHandler = (event: MouseEvent<HTMLElement | SVGSVGElement>) => {
    closeCalendar(event);

    const hasStart = startDate || startDatePreset;
    const hasEnd = endDate || nightsPreset || nights;

    if (hasStart && hasEnd && onChange) {
      onChange({
        left,
        right,
        inputFocuses,
        startDatePreset,
        nightsPreset,
        nights,
        shift,
        initialTab,
        buttonsClicked,
      });
    }
  };

  const { indicationMap, setStartCurrentMonth } = useFetchIndication({
    isMobile: Boolean(isMobile),
    searchType,
    departureId,
    placeId,
    calendarOpened: localOpen,
    startDate,
  });

  return (
    <DatePickerContainer $isMobile={isMobile} $isWizard={isWizard} className={className}>
      {localOpen && isMobile && !isWizard ? <OverflowRestrictor /> : null}
      <DurationTrip
        leftValue={left}
        rightValue={right}
        shift={shift}
        startDatePreset={startDatePreset}
        nights={nights}
        nightsPreset={nightsPreset}
        openCalendar={openCalendar}
        isMobile={isMobile}
        isWizard={isWizard}
        searchType={searchType}
        isOpenCalendar={localOpen}
        onStartDateClick={handleClickStartInput}
        onEndDateClick={handleClickEndInput}
        onStartDateClear={handleStartDateClear}
        onEndDateClear={handleEndDateClear}
        isStartDateFocus={isStartDateFocus}
        isEndDateFocus={isEndDateFocus}
        variant={variant}
      />
      <DatePickerTabs
        isFlexDatesOff={variant === 'hotel'}
        open={localOpen}
        closeCalendar={closeCalendarHandler}
        leftValue={left}
        rightValue={right}
        startDatePreset={startDatePreset}
        nightsPreset={nightsPreset}
        nights={nights}
        onChangeCalendar={onChangeCalendar}
        onChangeVacationOption={onChangeVacationOption}
        onButtonClick={handleButtonClick}
        currentShift={shift}
        onChangeShift={changeShift}
        excludeDates={excludeDates}
        searchCalendarDuration={searchCalendarDuration}
        applyButtonHandler={applyButtonHandler}
        minDate={minDate}
        isMobile={isMobile}
        isWizard={isWizard}
        isEndDateFocus={isEndDateFocus}
        isHotelOnly={searchType === SEARCH_TYPE_NAMES.HOTEL}
        indicationMap={indicationMap}
        setStartCurrentMonth={setStartCurrentMonth}
        vacationOptionsPresets={vacationOptionsPresets}
      />
    </DatePickerContainer>
  );
};

const DatePickerContainer = styled.div<{
  $isMobile?: boolean;
  $isWizard?: boolean;
}>`
  display: flex;
  flex-direction: column;
  order: 0;

  ${({ $isWizard }) =>
    !$isWizard &&
    `
    width: 100%;
    height: 100%;
  `}

  ${({ $isMobile }) =>
    $isMobile &&
    css`
      overflow: hidden;
      order: 1;
      align-items: center;
    `}
`;

const OverflowRestrictor = createGlobalStyle`
  body {
    overflow: hidden;
  }
`;
